import { Component, OnInit, inject, signal, computed, WritableSignal } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { PushNotificationService } from 'src/app/services/push-notifications/push-notification.service';

export interface NotificationSchedule {
  timeTagPrefix: string;
  daysTagPrefix: string;
  time: WritableSignal<string>;
  days: WritableSignal<number[]>;
}

export interface NotificationPreference {
  key: string;
  tag: string;
  checked: WritableSignal<boolean>;
  schedule?: NotificationSchedule;
}

interface WeekdayOption {
  value: number;
  nameKey: string;
}

const DEFAULT_COUNT_TIME = '09:00';
const DEFAULT_COUNT_DAYS = [1]; //reprents Monday

@Component({
  selector: 'app-notification-preferences-modal',
  templateUrl: './notification-preferences-modal.component.html',
  styleUrls: ['./notification-preferences-modal.component.scss'],
})
export class NotificationPreferencesModalComponent implements OnInit {
  private modalController = inject(ModalController);
  private pushNotificationService = inject(PushNotificationService);
  private datetimeFormatService = inject(DatetimeFormatService);

  readonly locale = this.datetimeFormatService.selectedDateTimeFormat;

  isLoading = signal(true);
  isSaving = signal(false);

  readonly weekdays: WeekdayOption[] = [
    { value: 1, nameKey: 'weekdayName.Mon' },
    { value: 2, nameKey: 'weekdayName.Tue' },
    { value: 3, nameKey: 'weekdayName.Wed' },
    { value: 4, nameKey: 'weekdayName.Thu' },
    { value: 5, nameKey: 'weekdayName.Fri' },
    { value: 6, nameKey: 'weekdayName.Sat' },
    { value: 7, nameKey: 'weekdayName.Sun' },
  ];

  preferences: NotificationPreference[] = [
    { key: 'bookings', tag: 'pref:Bookings', checked: signal(true) },
    { key: 'invitations', tag: 'pref:Invitations', checked: signal(true) },
    { key: 'updates', tag: 'pref:Updates', checked: signal(true) },
    { key: 'reminders', tag: 'pref:Reminders', checked: signal(true) },
    {
      key: 'invitationsCount',
      tag: 'pref:InvitationsCount',
      checked: signal(true),
      schedule: {
        timeTagPrefix: 'pref:InvitationsCountTime:',
        daysTagPrefix: 'pref:InvitationsCountDays:',
        time: signal(DEFAULT_COUNT_TIME),
        days: signal([...DEFAULT_COUNT_DAYS]),
      },
    },
  ];

  private originalState = signal('');

  hasChanges = computed(() => this.currentState() !== this.originalState());

  isApplyDisabled = computed(() => {
    const missingDays = this.preferences.some(
      (pref) => pref.schedule && pref.checked() && pref.schedule.days().length === 0
    );
    return !this.hasChanges() || this.isSaving() || this.isLoading() || missingDays;
  });

  async ngOnInit(): Promise<void> {
    try {
      const tags = await this.pushNotificationService.getPreferences();

      for (const pref of this.preferences) {
        pref.checked.set(tags.includes(pref.tag));
        this.applyScheduleFromTags(pref, tags);
      }

      this.originalState.set(this.currentState());
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  onToggle(pref: NotificationPreference, checked: boolean): void {
    pref.checked.set(checked);
    if (checked && pref.schedule && pref.schedule.days().length === 0) {
      pref.schedule.days.set([...DEFAULT_COUNT_DAYS]);
    }
  }

  toggleDay(schedule: NotificationSchedule, day: number): void {
    if (this.isSaving()) return;

    const selected = new Set(schedule.days());
    if (selected.has(day)) {
      selected.delete(day);
    } else {
      selected.add(day);
    }
    schedule.days.set([...selected].sort((a, b) => a - b));
  }

  onTimeChange(schedule: NotificationSchedule, event: CustomEvent<{ value?: string | string[] | null }>): void {
    const raw = event.detail.value;
    const value = Array.isArray(raw) ? raw[0] : raw;
    const time = this.toTimeString(value);
    if (!time) return;
    schedule.time.set(time);
  }

  timePickerValue(schedule: NotificationSchedule): string {
    return `1970-01-01T${schedule.time()}:00`;
  }

  isDaySelected(schedule: NotificationSchedule, day: number): boolean {
    return schedule.days().includes(day);
  }

  async applyPreferences(): Promise<void> {
    if (this.isApplyDisabled()) return;

    this.isSaving.set(true);
    try {
      await this.pushNotificationService.savePreferences(this.toTags());
      this.dismissModal();
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
    } finally {
      this.isSaving.set(false);
    }
  }

  dismissModal(): void {
    this.modalController.dismiss();
  }

  private currentState(): string {
    return JSON.stringify(
      this.preferences.map((pref) => ({
        checked: pref.checked(),
        time: pref.schedule?.time(),
        days: pref.schedule ? [...pref.schedule.days()].sort((a, b) => a - b) : undefined,
      }))
    );
  }

  private toTags(): string[] {
    const tags = this.preferences.filter((pref) => pref.checked()).map((pref) => pref.tag);

    for (const pref of this.preferences) {
      if (!pref.schedule) continue;
      tags.push(`${pref.schedule.timeTagPrefix}${pref.schedule.time()}`);
      if (pref.schedule.days().length > 0) {
        tags.push(`${pref.schedule.daysTagPrefix}${pref.schedule.days().join('-')}`);
      }
    }

    return tags;
  }

  private applyScheduleFromTags(pref: NotificationPreference, tags: string[]): void {
    if (!pref.schedule) return;

    const time = this.parseTime(tags, pref.schedule.timeTagPrefix);
    pref.schedule.time.set(time ?? DEFAULT_COUNT_TIME);

    const days = this.parseDays(tags, pref.schedule.daysTagPrefix);
    pref.schedule.days.set(days.length > 0 ? days : [...DEFAULT_COUNT_DAYS]);
  }

  private toTimeString(value: string | null | undefined): string | null {
    if (!value) return null;

    const isoMatch = value.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}:${isoMatch[2]}`;
    }

    const timeMatch = value.match(/^(\d{1,2}):(\d{2})/);
    if (!timeMatch) return null;

    return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
  }

  private parseTime(tags: string[], prefix: string): string | null {
    const timeTag = tags.find((tag) => tag.startsWith(prefix));
    return this.toTimeString(timeTag?.slice(prefix.length));
  }

  private parseDays(tags: string[], prefix: string): number[] {
    const daysTag = tags.find((tag) => tag.startsWith(prefix));
    if (!daysTag) return [];

    return daysTag
      .slice(prefix.length)
      .split('-')
      .map((part) => Number(part))
      .filter((day) => Number.isInteger(day) && day >= 1 && day <= 7);
  }
}
